import { useEffect } from "react";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  canonical?: string;
  schema?: Record<string, unknown>;
  noIndex?: boolean;
}

const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";

export default function SEO({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogType = "website",
  twitterCard = "summary_large_image",
  twitterTitle,
  twitterDescription,
  canonical,
  schema,
  noIndex = false,
}: SEOProps) {
  useEffect(() => {
    // Update document title
    if (title) {
      document.title = title;
    }

    // Helper function to update or create meta tag
    const updateMetaTag = (name: string, content: string, property = false) => {
      const attr = property ? "property" : "name";
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    // Update meta tags
    if (description) {
      updateMetaTag("description", description);
    }
    if (keywords) {
      updateMetaTag("keywords", keywords);
    }
    if (ogTitle) {
      updateMetaTag("og:title", ogTitle, true);
    }
    if (ogDescription) {
      updateMetaTag("og:description", ogDescription, true);
    }
    if (ogType) {
      updateMetaTag("og:type", ogType, true);
    }
    updateMetaTag("og:url", canonical || siteUrl, true);
    if (twitterCard) {
      updateMetaTag("twitter:card", twitterCard);
    }
    if (twitterTitle) {
      updateMetaTag("twitter:title", twitterTitle);
    }
    if (twitterDescription) {
      updateMetaTag("twitter:description", twitterDescription);
    }

    // Update canonical link
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }

    // Add noindex if needed
    if (noIndex) {
      updateMetaTag("robots", "noindex, nofollow");
    }

    // Add Schema.org JSON-LD
    if (schema) {
      const existingScript = document.querySelector(
        'script[type="application/ld+json"]'
      );
      if (existingScript) {
        existingScript.remove();
      }
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    // Cleanup function
    return () => {
      // Note: We don't remove tags on unmount to prevent flickering
      // The next page's SEO component will overwrite them
    };
  }, [
    title,
    description,
    keywords,
    ogTitle,
    ogDescription,
    ogType,
    twitterCard,
    twitterTitle,
    twitterDescription,
    canonical,
    schema,
    noIndex,
  ]);

  return null;
}